import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { formatDate, capitalize } from '../utils/helpers';
import api from '../utils/api';
import { toast } from 'react-toastify';
import CustomerChatBubble from '../components/CustomerChatBubble';

// ⚠ Dev shortcut: cards are captured directly into our DB instead of through
// Stripe. Replace with Stripe Elements before production.

// ── Order history: forest-green theme ───────────────────────────────────────
const FOREST = '#2d6a4f';

const TRACKING_STATUS_LABEL = {
  unknown:              { label: 'Unknown',             dot: '#9ca3af' },
  pre_transit:          { label: 'Pre-Transit',         dot: '#f59e0b' },
  in_transit:           { label: 'In Transit',          dot: '#f59e0b' },
  out_for_delivery:     { label: 'Out for Delivery',    dot: '#3b82f6' },
  delivered:            { label: 'Delivered',           dot: FOREST },
  available_for_pickup: { label: 'Available for Pickup', dot: '#8b5cf6' },
  return_to_sender:     { label: 'Return to Sender',    dot: '#ef4444' },
  failure:              { label: 'Delivery Issue',      dot: '#ef4444' },
  cancelled:            { label: 'Cancelled',           dot: '#9ca3af' },
  error:                { label: 'Error',               dot: '#ef4444' },
};

function TrackingSection({ order }) {
  if (!order.trackingNumber) return null;
  const te = order.trackingEvents;
  const statusInfo = te?.status
    ? (TRACKING_STATUS_LABEL[te.status] || { label: capitalize(te.status), dot: '#9ca3af' })
    : null;
  const latestEvent = te?.events?.[te.events.length - 1];

  const relativeTime = (iso) => {
    if (!iso) return '';
    const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(212,175,55,0.2)' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-base">📦</span>
        {order.carrier && (
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#7a5c30' }}>{order.carrier}</span>
        )}
        <span className="font-mono text-xs px-2 py-0.5 rounded tracking-wide"
          style={{ background: 'rgba(212,175,55,0.1)', color: '#d4af37', border: '1px solid rgba(212,175,55,0.2)' }}>
          {order.trackingNumber}
        </span>
        {statusInfo && (
          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: statusInfo.dot }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusInfo.dot, display: 'inline-block', flexShrink: 0 }} />
            {statusInfo.label}
          </span>
        )}
        {te?.publicUrl && (
          <a href={te.publicUrl} target="_blank" rel="noopener noreferrer"
            className="text-xs font-semibold ml-auto" style={{ color: '#d4af37' }}>
            Track &rarr;
          </a>
        )}
      </div>
      {latestEvent && (
        <div className="mt-1.5 text-xs flex items-start gap-1.5" style={{ color: '#7a5c30' }}>
          <span style={{ color: '#d4af37', marginTop: 2, flexShrink: 0 }}>●</span>
          <span>
            <span className="font-medium" style={{ color: '#a07840' }}>{latestEvent.description}</span>
            {latestEvent.location && <span style={{ color: '#5a3a18' }}> — {latestEvent.location}</span>}
          </span>
        </div>
      )}
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        {te?.estimatedDelivery && (
          <span className="text-xs" style={{ color: '#5a3a18' }}>
            Est. delivery:{' '}
            <strong style={{ color: '#7a5c30' }}>
              {new Date(te.estimatedDelivery).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
            </strong>
          </span>
        )}
        {te?.queriedAt && (
          <span className="text-[10px]" style={{ color: '#3a2510' }}>Updated {relativeTime(te.queriedAt)}</span>
        )}
      </div>
    </div>
  );
}

// ── Gold celebration overlay ─────────────────────────────────────────────────
function GoldCelebration({ onDone }) {
  const canvasRef = useRef(null);
  const doneRef   = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      @keyframes _goldFlash {
        0%   { opacity: 0;   transform: scale(0.6); }
        15%  { opacity: 1;   transform: scale(1.15); }
        55%  { opacity: 0.7; transform: scale(1.05); }
        100% { opacity: 0;   transform: scale(2.2); }
      }
      @keyframes _goldText {
        0%   { opacity: 0; transform: translateY(28px) scale(0.85); }
        25%  { opacity: 1; transform: translateY(0)   scale(1);    }
        70%  { opacity: 1; transform: translateY(0)   scale(1);    }
        100% { opacity: 0; transform: translateY(-22px) scale(1.08); }
      }
    `;
    document.head.appendChild(styleEl);

    const canvas = canvasRef.current;
    if (!canvas) {
      return () => { if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl); };
    }
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const GOLDS = ['#FFD700','#FFC107','#d4af37','#B8860B','#DAA520','#F5DEB3','#FFFACD','#FFF8DC','#fffdf0'];

    const drawStar = (c, r) => {
      c.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
        const rad = i % 2 === 0 ? r : r / 2;
        c.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
      }
      c.closePath(); c.fill();
    };

    const makeParticle = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height * 0.5 - canvas.height * 0.6,
      size: Math.random() * 14 + 5,
      color: GOLDS[Math.floor(Math.random() * GOLDS.length)],
      rot: Math.random() * 360,
      rotV: (Math.random() - 0.5) * 14,
      vx: (Math.random() - 0.5) * 5,
      vy: Math.random() * 5 + 2,
      shape: ['rect','circle','diamond','star'][Math.floor(Math.random() * 4)],
      shimmer: Math.random() > 0.45,
    });

    const particles = Array.from({ length: 280 }, makeParticle);
    const DURATION = 4200;
    const start = performance.now();
    let animId;

    const draw = (now) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const elapsed = now - start;
      const t = Math.min(elapsed / DURATION, 1);

      for (const p of particles) {
        p.y += p.vy;
        p.x += p.vx + Math.sin(elapsed / 480 + p.x * 0.01) * 0.4;
        p.rot += p.rotV;
        if (p.y > canvas.height + 20) { p.y = -20; p.x = Math.random() * canvas.width; }

        const shine = p.shimmer ? 0.55 + 0.45 * Math.sin(elapsed / 130 + p.x) : 1;
        const alpha = t > 0.62 ? ((1 - t) / 0.38) * shine : shine;

        ctx.save();
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 5, p.size, p.size / 2.5);
        } else if (p.shape === 'circle') {
          ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill();
        } else if (p.shape === 'diamond') {
          ctx.beginPath();
          ctx.moveTo(0, -p.size / 2); ctx.lineTo(p.size / 2, 0);
          ctx.lineTo(0, p.size / 2);  ctx.lineTo(-p.size / 2, 0);
          ctx.closePath(); ctx.fill();
        } else {
          drawStar(ctx, p.size / 2);
        }
        ctx.restore();
      }

      if (t < 1) { animId = requestAnimationFrame(draw); }
      else        { doneRef.current?.(); }
    };

    animId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
      {/* Primary gold radial burst */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 44%, rgba(255,215,0,0.60) 0%, rgba(212,175,55,0.35) 28%, transparent 68%)',
        animation: '_goldFlash 1.3s ease-out forwards',
      }} />
      {/* Second softer wave */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 44%, rgba(255,248,220,0.45) 0%, transparent 52%)',
        animation: '_goldFlash 0.75s 0.08s ease-out forwards',
      }} />
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      {/* Epic headline */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        animation: '_goldText 4.2s ease-out forwards',
      }}>
        <div style={{
          fontSize: 'clamp(2rem, 5vw, 3.4rem)',
          fontWeight: 900,
          letterSpacing: '0.14em',
          background: 'linear-gradient(135deg, #FFD700 0%, #FFF8DC 38%, #d4af37 62%, #FFD700 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          filter: 'drop-shadow(0 0 28px rgba(255,215,0,0.9)) drop-shadow(0 0 60px rgba(255,215,0,0.5))',
          textAlign: 'center',
        }}>
          YOU'RE LOCKED IN
        </div>
        <div style={{
          marginTop: '0.6rem',
          fontSize: 'clamp(0.75rem, 1.5vw, 1rem)',
          fontWeight: 700,
          letterSpacing: '0.35em',
          color: 'rgba(255,215,0,0.92)',
          textTransform: 'uppercase',
          filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.8))',
        }}>
          ✦ &nbsp; Subscription Activated &nbsp; ✦
        </div>
      </div>
    </div>
  );
}

// ── Stripe card setup form — must render inside <Elements> ──────────────────
const BILLING_INPUT_STYLE = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: '8px',
  padding: '10px 12px',
  color: '#f5e6c8',
  fontSize: '14px',
  outline: 'none',
  width: '100%',
};
const BILLING_LABEL_STYLE = { color: '#d4af37', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6, display: 'block' };

function CardSetupForm({ sub, user, onSuccess }) {
  const [billingDay, setBillingDay] = useState(1);
  const [agreed,     setAgreed]     = useState(false);
  const [loading,    setLoading]    = useState(false);

  // Card capture (no Stripe — see file header)
  const [cardNumber,   setCardNumber]   = useState('');
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear,  setCardExpYear]  = useState('');
  const [cardCvc,      setCardCvc]      = useState('');

  // Billing identity
  const defaultName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const [billingName,       setBillingName]       = useState(defaultName);
  const [billingLine1,      setBillingLine1]      = useState('');
  const [billingLine2,      setBillingLine2]      = useState('');
  const [billingCity,       setBillingCity]       = useState('');
  const [billingProvince,   setBillingProvince]   = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingCountry,    setBillingCountry]    = useState('CA');

  const monthly = Math.round(
    (sub.variant?.priceInCents || 0) * sub.quantity *
    (1 - (parseFloat(sub.discountPercent) || 15) / 100)
  );

  // Strip spaces from card number, validate length
  const rawCardNumber = cardNumber.replace(/\s+/g, '');
  const cardComplete =
    /^\d{12,19}$/.test(rawCardNumber) &&
    /^\d{1,2}$/.test(cardExpMonth) && Number(cardExpMonth) >= 1 && Number(cardExpMonth) <= 12 &&
    /^\d{4}$/.test(cardExpYear) &&
    /^\d{3,4}$/.test(cardCvc);

  const addressComplete =
    billingName.trim() && billingLine1.trim() && billingCity.trim() &&
    billingProvince.trim() && billingPostalCode.trim() && billingCountry.trim().length === 2;

  const canSubmit = cardComplete && agreed && addressComplete && !loading;

  // Pretty-print card number as user types (4-4-4-4)
  const handleCardNumberChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
    setCardNumber(digits.replace(/(.{4})/g, '$1 ').trim());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const country = billingCountry.trim().toUpperCase();

      // Single backend call — card data goes straight to our DB
      const confirmRes = await api.post(`/subscriptions/${sub.id}/confirm-card`, {
        cardNumber:   rawCardNumber,
        cardExpMonth: Number(cardExpMonth),
        cardExpYear:  Number(cardExpYear),
        cardCvc:      cardCvc.trim(),
        billingDay,
        billingName:       billingName.trim(),
        billingLine1:      billingLine1.trim(),
        billingLine2:      billingLine2.trim() || null,
        billingCity:       billingCity.trim(),
        billingProvince:   billingProvince.trim(),
        billingPostalCode: billingPostalCode.trim(),
        billingCountry:    country,
      });

      toast.success(
        `Billing activated! You'll be charged on the ${billingDay === 1 ? '1st' : '15th'} of each month.`,
        { autoClose: 5000 }
      );
      // Fire the celebration BEFORE telling the parent to swap to the success
      // branch — the parent rerender otherwise unmounts this form mid-state-update
      // and the confetti never gets to render.
      onSuccess(confirmRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to set up billing');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      {/* Billing day selector */}
      <div>
        <p style={{ color: '#d4af37' }} className="text-xs font-bold uppercase tracking-widest mb-2">
          Choose your billing date
        </p>
        <div className="flex gap-3">
          {[1, 15].map(day => (
            <button
              key={day}
              type="button"
              onClick={() => setBillingDay(day)}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
              style={billingDay === day ? {
                background: 'linear-gradient(135deg, #d4af37 0%, #f5c842 100%)',
                color: '#0a0718',
                boxShadow: '0 0 12px rgba(212,175,55,0.5)',
              } : {
                border: '1px solid rgba(212,175,55,0.3)',
                color: 'rgba(212,175,55,0.6)',
                background: 'transparent',
              }}
            >
              {day === 1 ? '1st of month' : '15th of month'}
            </button>
          ))}
        </div>
      </div>

      {/* Billing identity */}
      <div className="pt-2" style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
        <p style={{ color: '#d4af37' }} className="text-xs font-bold uppercase tracking-widest mb-3 mt-3">
          Billing details
        </p>
        <div className="space-y-3">
          <div>
            <label style={BILLING_LABEL_STYLE}>Name on card</label>
            <input
              type="text"
              value={billingName}
              onChange={e => setBillingName(e.target.value)}
              placeholder="Full legal name"
              maxLength={120}
              autoComplete="cc-name"
              style={BILLING_INPUT_STYLE}
              required
            />
          </div>
          <div>
            <label style={BILLING_LABEL_STYLE}>Street address</label>
            <input
              type="text"
              value={billingLine1}
              onChange={e => setBillingLine1(e.target.value)}
              placeholder="123 Main St"
              maxLength={255}
              autoComplete="billing address-line1"
              style={BILLING_INPUT_STYLE}
              required
            />
          </div>
          <div>
            <label style={BILLING_LABEL_STYLE}>Apt / suite (optional)</label>
            <input
              type="text"
              value={billingLine2}
              onChange={e => setBillingLine2(e.target.value)}
              placeholder="Apt 4B"
              maxLength={255}
              autoComplete="billing address-line2"
              style={BILLING_INPUT_STYLE}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={BILLING_LABEL_STYLE}>City</label>
              <input
                type="text"
                value={billingCity}
                onChange={e => setBillingCity(e.target.value)}
                placeholder="City"
                maxLength={120}
                autoComplete="billing address-level2"
                style={BILLING_INPUT_STYLE}
                required
              />
            </div>
            <div>
              <label style={BILLING_LABEL_STYLE}>Province / state</label>
              <input
                type="text"
                value={billingProvince}
                onChange={e => setBillingProvince(e.target.value)}
                placeholder="ON"
                maxLength={80}
                autoComplete="billing address-level1"
                style={BILLING_INPUT_STYLE}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={BILLING_LABEL_STYLE}>Postal / ZIP</label>
              <input
                type="text"
                value={billingPostalCode}
                onChange={e => setBillingPostalCode(e.target.value)}
                placeholder="M5V 2T6"
                maxLength={20}
                autoComplete="billing postal-code"
                style={BILLING_INPUT_STYLE}
                required
              />
            </div>
            <div>
              <label style={BILLING_LABEL_STYLE}>Country</label>
              <input
                type="text"
                value={billingCountry}
                onChange={e => setBillingCountry(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="CA"
                maxLength={2}
                autoComplete="billing country"
                style={{ ...BILLING_INPUT_STYLE, textTransform: 'uppercase' }}
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Card capture (direct — no Stripe) */}
      <div className="pt-2" style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
        <p style={{ color: '#d4af37' }} className="text-xs font-bold uppercase tracking-widest mb-3 mt-3">
          Card details
        </p>
        <div className="space-y-3">
          <div>
            <label style={BILLING_LABEL_STYLE}>Card number</label>
            <input
              type="text"
              value={cardNumber}
              onChange={handleCardNumberChange}
              placeholder="1234 5678 9012 3456"
              inputMode="numeric"
              autoComplete="cc-number"
              style={{ ...BILLING_INPUT_STYLE, letterSpacing: '0.08em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label style={BILLING_LABEL_STYLE}>Exp month</label>
              <input
                type="text"
                value={cardExpMonth}
                onChange={e => setCardExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                placeholder="MM"
                inputMode="numeric"
                maxLength={2}
                autoComplete="cc-exp-month"
                style={BILLING_INPUT_STYLE}
                required
              />
            </div>
            <div>
              <label style={BILLING_LABEL_STYLE}>Exp year</label>
              <input
                type="text"
                value={cardExpYear}
                onChange={e => setCardExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="YYYY"
                inputMode="numeric"
                maxLength={4}
                autoComplete="cc-exp-year"
                style={BILLING_INPUT_STYLE}
                required
              />
            </div>
            <div>
              <label style={BILLING_LABEL_STYLE}>CVC</label>
              <input
                type="text"
                value={cardCvc}
                onChange={e => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="123"
                inputMode="numeric"
                maxLength={4}
                autoComplete="cc-csc"
                style={BILLING_INPUT_STYLE}
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Authorization checkbox */}
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-amber-400 shrink-0"
        />
        <span className="text-xs leading-relaxed" style={{ color: '#a08060' }}>
          I authorize Aloha Amino to charge{' '}
          <strong style={{ color: '#d4af37' }}>
            ${(monthly / 100).toFixed(2)} USD
          </strong>{' '}
          on the {billingDay === 1 ? '1st' : '15th'} of each month, recurring automatically until I cancel.
        </span>
      </label>

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full py-3 rounded-lg font-bold text-sm tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: canSubmit
            ? 'linear-gradient(135deg, #b8860b 0%, #d4af37 45%, #f5c842 100%)'
            : 'rgba(212,175,55,0.2)',
          color: canSubmit ? '#0a0718' : 'rgba(212,175,55,0.5)',
          boxShadow: canSubmit ? '0 0 16px rgba(212,175,55,0.4)' : 'none',
        }}
      >
        {loading ? 'Activating…' : '✦ Activate Subscription Billing'}
      </button>
    </form>
  );
}

// ── Long thin gold info bar — sits under My Subscriptions, hover-to-expand ──
function BillingTimelineBar() {
  const [open, setOpen] = useState(false);
  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className={`relative ${open ? 'rounded-2xl' : 'rounded-full'} mt-4 gold-bar-glow overflow-hidden transition-[border-radius] duration-200`}
      style={{
        background: 'linear-gradient(135deg, #b8860b 0%, #d4af37 35%, #f5c842 50%, #d4af37 65%, #b8860b 100%)',
        border: '1px solid rgba(255,248,220,0.55)',
      }}
    >
      {/* Animated shimmer overlay */}
      <div className={`absolute inset-0 gold-bar-shimmer ${open ? 'rounded-2xl' : 'rounded-full'}`} />

      <div
        tabIndex={0}
        role="button"
        aria-expanded={open}
        className="relative w-full flex items-center justify-center gap-2 px-5 py-2 text-[12px] font-black uppercase tracking-[0.18em] cursor-pointer outline-none"
        style={{ color: '#0a0718', textShadow: '0 1px 0 rgba(255,248,220,0.4)' }}
      >
        <span>✦</span>
        <span>How &amp; when you're billed and shipped</span>
        <span
          style={{
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
            fontSize: '14px',
            lineHeight: 1,
          }}
        >
          ⌄
        </span>
      </div>

      {open && (
        <div
          className="relative px-5 pb-4 pt-1 text-[12.5px] leading-relaxed"
          style={{ color: '#0a0718' }}
        >
          <div
            className="rounded-xl px-4 py-3 space-y-2"
            style={{
              background: 'rgba(10,7,24,0.86)',
              border: '1px solid rgba(255,248,220,0.35)',
              color: '#f5e6c8',
            }}
          >
            <div className="flex items-start gap-2">
              <span style={{ color: '#FFD700' }}>●</span>
              <p>
                <strong style={{ color: '#FFD700' }}>First charge is today.</strong>{' '}
                Your initial purchase ships out{' '}
                <strong style={{ color: '#FFD700' }}>immediately</strong> — no waiting on a billing date.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span style={{ color: '#FFD700' }}>●</span>
              <p>
                After that, your subscription item ships on the{' '}
                <strong style={{ color: '#FFD700' }}>1st or the 15th</strong>{' '}
                of each month — whichever billing date you picked, once your payment clears.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Subscription message thread (admin responses to the sub's orders) ─────────
function SubscriptionMessages({ subId }) {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/subscriptions/' + subId + '/messages');
      setMessages(res.data.messages || []);
    } catch {
      toast.error('Failed to load messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null) load();
  };

  const label = (m) =>
    m.senderType === 'admin'  ? '✦ Aloha Amino Team' :
    m.senderType === 'system' ? 'System'          : 'You';

  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 text-xs font-bold transition-opacity hover:opacity-80"
        style={{ color: '#d4af37' }}
      >
        <span>💬</span>
        <span>Messages from our team{messages ? ' (' + messages.length + ')' : ''}</span>
        <span className="text-[9px]">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
          {loading ? (
            <div className="text-xs" style={{ color: '#7a5c30' }}>Loading…</div>
          ) : !messages || messages.length === 0 ? (
            <div className="text-xs" style={{ color: '#7a5c30' }}>
              No messages yet on this subscription's orders.
            </div>
          ) : (
            messages.map(m => (
              <div
                key={m.id}
                className="rounded-lg px-3 py-2"
                style={{
                  background: m.senderType === 'admin' ? 'rgba(212,175,55,0.10)' : 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(212,175,55,0.12)',
                }}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: m.senderType === 'admin' ? '#d4af37' : '#7a5c30' }}
                  >
                    {label(m)}
                  </span>
                  {m.orderNumber && (
                    <span className="text-[9px]" style={{ color: '#5a3a18' }}>Order #{m.orderNumber}</span>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap break-words" style={{ color: '#e8d9b8' }}>
                  {m.body}
                </div>
                <div className="text-[9px] mt-1" style={{ color: '#5a3a18' }}>
                  {new Date(m.createdAt).toLocaleString('en-CA', {
                    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Per-order message thread (admin responses to a single order) ─────────────
function OrderMessages({ orderId }) {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders/" + orderId + "/messages");
      setMessages(res.data.messages || []);
    } catch {
      toast.error("Failed to load messages");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null) load();
  };

  const label = (m) =>
    m.senderType === "admin"  ? "✦ Aloha Amino Team" :
    m.senderType === "system" ? "System"          : "You";

  return (
    <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(212,175,55,0.15)" }}>
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 text-xs font-bold transition-opacity hover:opacity-80"
        style={{ color: "#d4af37" }}
      >
        <span>💬</span>
        <span>Messages from our team{messages ? " (" + messages.length + ")" : ""}</span>
        <span className="text-[9px]">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
          {loading ? (
            <div className="text-xs" style={{ color: "#7a5c30" }}>Loading…</div>
          ) : !messages || messages.length === 0 ? (
            <div className="text-xs" style={{ color: "#7a5c30" }}>
              No messages yet on this order.
            </div>
          ) : (
            messages.map(m => (
              <div
                key={m.id}
                className="rounded-lg px-3 py-2"
                style={{
                  background: m.senderType === "admin" ? "rgba(212,175,55,0.10)" : "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(212,175,55,0.12)",
                }}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: m.senderType === "admin" ? "#d4af37" : "#7a5c30" }}
                  >
                    {label(m)}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap break-words" style={{ color: "#e8d9b8" }}>
                  {m.body}
                </div>
                <div className="text-[9px] mt-1" style={{ color: "#5a3a18" }}>
                  {new Date(m.createdAt).toLocaleString("en-CA", {
                    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Epic golden subscription tile ───────────────────────────────────────────
function EpicSubscriptionTile({ sub, user, onUpdate, onStatusChange }) {
  const hasCard = !!sub.stripePaymentMethodId;
  const [showCelebration, setShowCelebration] = useState(false);

  const handleCardSuccess = (updatedSub) => {
    setShowCelebration(true);
    onUpdate(updatedSub);
  };

  const monthly = Math.round(
    (sub.variant?.priceInCents || 0) * sub.quantity *
    (1 - (parseFloat(sub.discountPercent) || 15) / 100)
  );

  return (
    <div
      className={`relative rounded-2xl p-6 ${hasCard ? 'epic-tile-active' : 'epic-tile-pending'}`}
      style={{
        background: 'linear-gradient(145deg, #070412 0%, #130b30 45%, #070412 100%)',
        border: `2px solid ${hasCard ? '#d4af37' : '#9a7010'}`,
      }}
    >
      {/* Subscribe & Save badge */}
      <div
        className="absolute top-3.5 right-4 text-[9px] font-black tracking-[0.15em] px-2 py-0.5 rounded"
        style={{
          background: 'linear-gradient(135deg, #b8860b, #d4af37, #f5c842)',
          color: '#0a0718',
        }}
      >
        ✦ SUBSCRIBE & SAVE
      </div>

      {/* Status badge */}
      <div className="absolute top-3.5 left-4">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          sub.status === 'active' ? 'bg-blue-900/60 text-blue-400' :
          sub.status === 'paused' ? 'bg-yellow-900/60 text-yellow-400' :
          'bg-gray-800 text-gray-400'
        }`}>
          {capitalize(sub.status)}
        </span>
      </div>

      {/* Product info */}
      <div className="mt-6 mb-4">
        <div className="font-bold text-xl" style={{ color: '#f5e6c8' }}>
          {sub.variant?.product?.name}
        </div>
        <div className="text-sm mt-1" style={{ color: '#7a5c30' }}>
          {sub.variant?.name}&nbsp;·&nbsp;Qty: {sub.quantity}&nbsp;·&nbsp;
          Every {sub.intervalMonths === 1 ? 'month' : `${sub.intervalMonths} months`}
        </div>
        <div className="flex items-baseline gap-3 mt-3">
          <span className="text-3xl font-black" style={{ color: '#d4af37' }}>
            ${(monthly / 100).toFixed(2)}
          </span>
          <span className="text-sm" style={{ color: '#7a5c30' }}>USD / month</span>
          <span className="text-xs line-through" style={{ color: '#4a3520' }}>
            ${((sub.variant?.priceInCents || 0) * sub.quantity / 100).toFixed(2)}
          </span>
          <span
            className="text-[11px] font-black px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(212,175,55,0.2)', color: '#d4af37' }}
          >
            {parseFloat(sub.discountPercent)}% OFF
          </span>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid rgba(212,175,55,0.2)', margin: '0 0 16px' }} />

      {/* Billing section */}
      {hasCard ? (
        <div className="flex items-center gap-2">
          <span style={{ color: '#60a5fa', fontSize: '16px' }}>✓</span>
          <span className="text-sm font-medium" style={{ color: '#86c97e' }}>
            {capitalize(sub.cardBrand || '')} ****{sub.cardLast4}&nbsp;·&nbsp;
            Bills on the {sub.billingDay === 1 ? '1st' : '15th'} of every month
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-block w-2 h-2 rounded-full animate-pulse"
              style={{ background: '#d4af37' }}
            />
            <p className="text-sm font-bold" style={{ color: '#d4af37' }}>
              Billing setup required to activate your subscription
            </p>
          </div>
          <p className="text-xs mb-3" style={{ color: '#7a5c30' }}>
            Add your card below to authorize monthly charges and lock in your {parseFloat(sub.discountPercent)}% discount.
          </p>
          <CardSetupForm sub={sub} user={user} onSuccess={handleCardSuccess} />
        </>
      )}

      {showCelebration && (
        <GoldCelebration onDone={() => setShowCelebration(false)} />
      )}

      {/* Next billing date */}
      {sub.nextBillingDate && (
        <div className="mt-3 text-xs" style={{ color: '#5a3a18' }}>
          Next billing:{' '}
          <strong style={{ color: '#7a5c30' }}>
            {new Date(sub.nextBillingDate).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
          </strong>
        </div>
      )}

      <SubscriptionMessages subId={sub.id} />

      {/* Actions */}
      <div className="flex gap-2 mt-5 pt-4" style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}>
        {sub.status === 'active' && (
          <button
            onClick={() => onStatusChange(sub.id, 'paused')}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:opacity-80"
            style={{ border: '1px solid rgba(212,175,55,0.25)', color: '#7a5c30', background: 'rgba(212,175,55,0.05)' }}
          >
            Pause
          </button>
        )}
        {sub.status === 'paused' && (
          <button
            onClick={() => onStatusChange(sub.id, 'active')}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:opacity-80"
            style={{ border: '1px solid rgba(96,165,250,0.3)', color: '#60a5fa', background: 'rgba(96,165,250,0.05)' }}
          >
            Reactivate
          </button>
        )}
        <button
          onClick={() => { if (window.confirm('Cancel this subscription?')) onStatusChange(sub.id, 'cancelled'); }}
          className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:opacity-80"
          style={{ border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', background: 'rgba(239,68,68,0.05)' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Admin-only: change password without email verification ──────────────────
function AdminSecurityCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving]                   = useState(false);

  const strongEnough = newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword);
  const canSubmit = currentPassword && strongEnough && newPassword === confirmPassword && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      toast.success('Password updated.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tea-500';

  return (
    <div className="mt-6 pt-6 border-t border-gray-100 text-left">
      <h3 className="text-sm font-bold text-gray-700 mb-1">🔒 Change password</h3>
      <p className="text-xs text-gray-500 mb-3">Update your admin password directly — no email verification needed.</p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <input type="password" autoComplete="current-password" placeholder="Current password"
          value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className={inputCls} />
        <input type="password" autoComplete="new-password" placeholder="New password"
          value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputCls} />
        <input type="password" autoComplete="new-password" placeholder="Confirm new password"
          value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputCls} />
        {newPassword && !strongEnough && (
          <p className="text-[11px] text-amber-600">Min 8 characters, one uppercase letter and one number.</p>
        )}
        {confirmPassword && newPassword !== confirmPassword && (
          <p className="text-[11px] text-red-600">Passwords don&apos;t match.</p>
        )}
        <button type="submit" disabled={!canSubmit}
          className="btn-primary w-full text-sm disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

// ── Privacy & Your Data: self-service export + account deletion ──────────────
// CCPA/GDPR rights, exposed to every customer. "Download my data" streams a JSON
// export; "Delete my account" anonymizes the account after a password confirm.
function PrivacyDataCard() {
  const { logout } = useAuthCtx();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword]     = useState('');
  const [ack, setAck]               = useState(false);
  const [busy, setBusy]             = useState(false);

  const downloadData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/auth/export-data');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aloha-amino-my-data.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Your data export has downloaded.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not export your data.');
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (!password || !ack || busy) return;
    setBusy(true);
    try {
      await api.post('/auth/delete-account', { password });
      toast.success('Your account and personal data have been deleted.');
      // Session is already revoked server-side; clear local state and leave.
      await logout();
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete your account.');
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-100 text-left">
      <h3 className="text-sm font-bold text-gray-700 mb-1">🛡️ Privacy &amp; your data</h3>
      <p className="text-xs text-gray-500 mb-3">
        Under U.S. (CCPA) and international privacy law you can download a copy of your
        data or delete your account at any time.
      </p>

      <button
        type="button"
        onClick={downloadData}
        disabled={busy}
        className="btn-secondary w-full text-sm mb-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Download my data
      </button>

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="w-full text-sm font-medium rounded-lg py-2 border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
        >
          Delete my account
        </button>
      ) : (
        <form onSubmit={deleteAccount} className="mt-1 rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
          <p className="text-[11px] text-red-700 leading-snug">
            This permanently removes your personal information and signs you out. Anonymized
            order records are kept only as long as tax and compliance law requires. This cannot
            be undone.
          </p>
          <label htmlFor="delete-confirm-password" className="sr-only">Confirm your password</label>
          <input
            id="delete-confirm-password"
            type="password"
            autoComplete="current-password"
            placeholder="Confirm your password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <label className="flex items-start gap-2 text-[11px] text-red-700 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
            <span>I understand this permanently deletes my personal data.</span>
          </label>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setConfirming(false); setPassword(''); setAck(false); }}
              className="flex-1 text-xs font-medium rounded-lg py-2 border border-gray-300 text-gray-600 hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!password || !ack || busy}
              className="flex-1 text-xs font-bold rounded-lg py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? 'Deleting…' : 'Permanently delete'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Text My Client: SMS opt-in panel (under My Subscriptions) ───────────────
function SmsUpdatesPanel() {
  const [phone, setPhone]           = useState('');
  const [textUpdates, setText]      = useState(false);
  const [vipUpdates, setVip]        = useState(false);
  const [providerLive, setProvider] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [testing, setTesting]       = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/sms/preferences');
        if (!alive) return;
        setPhone(data.smsPhone || '');
        setText(!!data.textUpdates);
        setVip(!!data.vipUpdates);
        setProvider(!!data.providerLive);
      } catch (_) {
        /* not logged in / not available — leave panel in default state */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async (next = {}) => {
    setSaving(true);
    try {
      const payload = {
        phone: next.phone !== undefined ? next.phone : phone,
        textUpdates: next.textUpdates !== undefined ? next.textUpdates : textUpdates,
        vipUpdates: next.vipUpdates !== undefined ? next.vipUpdates : vipUpdates,
      };
      const { data } = await api.put('/sms/preferences', payload);
      setPhone(data.smsPhone || '');
      setText(!!data.textUpdates);
      setVip(!!data.vipUpdates);
      setProvider(!!data.providerLive);
      toast.success('Text preferences saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save text preferences.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (which) => {
    const nextVal = which === 'text' ? !textUpdates : !vipUpdates;
    if (which === 'text') setText(nextVal); else setVip(nextVal);
    save({ [which === 'text' ? 'textUpdates' : 'vipUpdates']: nextVal });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/sms/test');
      toast.info(data.message || 'Test queued.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send test.');
    } finally {
      setTesting(false);
    }
  };

  const Row = ({ on, onClick, emoji, title, desc }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      className="w-full flex items-center gap-3 text-left rounded-xl px-3 py-2.5 transition disabled:opacity-50"
      style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.18)' }}
    >
      <span className="text-lg shrink-0">{emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: '#e8c97a' }}>{title}</div>
        <div className="text-[11px] leading-snug" style={{ color: '#9a8050' }}>{desc}</div>
      </div>
      <span
        className="shrink-0 rounded-full transition-all"
        style={{
          width: 38, height: 22, padding: 2,
          background: on ? '#60a5fa' : 'rgba(255,255,255,0.15)',
          display: 'inline-flex', justifyContent: on ? 'flex-end' : 'flex-start',
        }}
      >
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff' }} />
      </span>
    </button>
  );

  if (loading) {
    return <div className="mb-4 rounded-2xl animate-pulse h-32" style={{ background: '#150d33' }} />;
  }

  return (
    <div
      className="mb-5 rounded-2xl p-4"
      style={{ background: 'linear-gradient(145deg, #0a0518, #160c34)', border: '1px solid rgba(212,175,55,0.28)' }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">💬</span>
        <h3 className="text-sm font-bold" style={{ color: '#f0d488' }}>Text me updates</h3>
        {!providerLive && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#9a8050' }}>
            preview
          </span>
        )}
      </div>
      <p className="text-[11px] mb-3" style={{ color: '#8a7048' }}>
        Choose what we text you. Standard message rates apply. Reply STOP anytime to opt out.
      </p>

      <input
        type="tel"
        inputMode="tel"
        placeholder="Mobile number (e.g. 555 123 4567)"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        onBlur={() => { if (phone) save({ phone }); }}
        className="w-full rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none"
        style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(212,175,55,0.25)', color: '#f5e9c8' }}
      />

      <div className="space-y-2">
        <Row
          on={textUpdates}
          onClick={() => toggle('text')}
          emoji="📦"
          title="Text Updates"
          desc="Order shipped, subscription renewing & billing reminders."
        />
        <Row
          on={vipUpdates}
          onClick={() => toggle('vip')}
          emoji="⭐"
          title="VIP Updates"
          desc="Early access to drops and VIP-only offers."
        />
      </div>

      {(textUpdates || vipUpdates) && phone && (
        <button
          type="button"
          onClick={sendTest}
          disabled={testing}
          className="mt-3 text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
          style={{ border: '1px solid rgba(96,165,250,0.35)', color: '#60a5fa' }}
        >
          {testing ? 'Sending…' : 'Send me a test text'}
        </button>
      )}
    </div>
  );
}

// ── VIP membership panel (left column of the account page) ──────────────────
function MembershipPanel({ membership, fmt, refreshUser }) {
  const [busy, setBusy] = useState(false);
  const m = membership;
  const active = m && m.active;

  const planLabel = m?.plan === 'monthly' ? 'Monthly VIP' : 'Annual VIP';
  const cadence   = m?.plan === 'monthly' ? 'month' : 'year';

  const cancel = async () => {
    if (!window.confirm('Cancel auto-renew? You keep VIP access until your current period ends.')) return;
    setBusy(true);
    try {
      await api.patch(`/memberships/${m.id}`, { status: 'cancelled' });
      await refreshUser();
      toast.success('Auto-renew cancelled. You keep VIP access until your period ends.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not cancel');
    } finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.patch(`/memberships/${m.id}`, { status: 'active' });
      await refreshUser();
      toast.success('Auto-renew turned back on.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resume');
    } finally { setBusy(false); }
  };

  if (!active) {
    return (
      <div className="text-center">
        <div className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-1">★ VIP Membership</div>
        <p className="text-sm text-gray-500 mb-3">
          {m && m.status === 'past_due'
            ? 'Your membership renewal is being processed.'
            : 'Unlock member pricing + free shipping on every order.'}
        </p>
        <Link to="/membership" className="btn-primary inline-block text-sm px-5 py-2">
          {m && (m.status === 'expired' || m.status === 'cancelled') ? 'Renew VIP' : 'Become a VIP'}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-600">★ VIP Member</span>
        <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 rounded-full px-2 py-0.5">Active</span>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Plan</span><span className="font-semibold text-gray-800">{planLabel}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="font-semibold text-gray-800">{fmt(m.priceInCents)}/{cadence}</span></div>
        <div className="flex justify-between">
          <span className="text-gray-500">{m.autoRenew ? 'Renews' : 'Access until'}</span>
          <span className="font-semibold text-gray-800">{m.currentPeriodEnd ? formatDate(m.currentPeriodEnd) : '—'}</span>
        </div>
        {m.cardLast4 && <div className="flex justify-between"><span className="text-gray-500">Card</span><span className="font-semibold text-gray-800">•••• {m.cardLast4}</span></div>}
      </div>
      {m.autoRenew ? (
        <button onClick={cancel} disabled={busy}
          className="mt-3 w-full text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50">
          {busy ? '…' : 'Cancel auto-renew'}
        </button>
      ) : (
        <button onClick={resume} disabled={busy}
          className="mt-3 w-full rounded-full border border-tea-300 text-tea-700 hover:bg-tea-50 text-xs font-bold py-2 transition-colors disabled:opacity-50">
          {busy ? '…' : 'Turn auto-renew back on'}
        </button>
      )}
    </div>
  );
}

// ── Main Account page ───────────────────────────────────────────────────────
export default function AccountPage() {
  const { user, refreshUser } = useAuthCtx();
  const { fmt } = useCurrency();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get('/orders')
      .then(res => setOrders(res.data.data))
      .catch(() => toast.error('Failed to load orders'));
  }, []);

  // Live chat bubble binds to the customer's most recent order.
  const chatOrder = orders.length
    ? [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main id="main-content" tabIndex={-1} className="flex-1 max-w-7xl mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-8">My Account</h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Profile card */}
          <div className="card md:col-span-1 h-fit">
            <div className="text-center">
              <div className="w-16 h-16 bg-tea-100 rounded-full flex items-center justify-center text-2xl font-bold text-tea-700 mx-auto mb-4">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <p className="font-bold text-lg">{user?.firstName} {user?.lastName}</p>
              <p className="text-gray-500 text-sm">{user?.email}</p>
              <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                <span className="inline-block badge-gray">
                  {capitalize(user?.role || '')}
                </span>
              </div>

            </div>

            {/* VIP membership */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <MembershipPanel membership={user?.membership} fmt={fmt} refreshUser={refreshUser} />
            </div>

            {user?.role === 'admin' && <AdminSecurityCard />}
            {user?.role !== 'admin' && <PrivacyDataCard />}
          </div>

          {/* Right column — order history */}
          <div className="md:col-span-3">

            {/* ── Order History ────────────────────────────────────────── */}
            <div>
              <h2 className="text-xl font-bold mb-4">Order History</h2>
              {orders.length === 0 ? (
                <div className="rounded-2xl text-center py-10" style={{
                  background: 'linear-gradient(145deg, #070412, #130b30)',
                  border: '1px solid rgba(212,175,55,0.2)',
                }}>
                  <div className="text-4xl mb-3">📦</div>
                  <p className="font-semibold" style={{ color: '#a07840' }}>No orders yet</p>
                  <p className="text-sm mt-1" style={{ color: '#5a3a18' }}>
                    Browse our catalog to place your first order.
                  </p>
                  <Link to="/shop" className="btn-primary mt-5 inline-block text-sm">Browse Catalog</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div
                      key={order.id}
                      className="relative rounded-2xl p-5 overflow-hidden"
                      style={{
                        background: 'linear-gradient(145deg, #070412 0%, #130b30 45%, #070412 100%)',
                        border: '2px solid rgba(212,175,55,0.4)',
                      }}
                    >
                      {/* Status badge — top left */}
                      <div className="absolute top-3.5 left-4">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          order.status === 'delivered' ? 'bg-blue-900/60 text-blue-400' :
                          order.status === 'shipped'   ? 'bg-blue-900/60 text-blue-400' :
                          order.status === 'processing' ? 'bg-yellow-900/60 text-yellow-400' :
                          order.status === 'cancelled' ? 'bg-red-900/60 text-red-400' :
                          'bg-gray-800 text-gray-400'
                        }`}>
                          {capitalize(order.status)}
                        </span>
                      </div>

                      {/* Order # badge — top right */}
                      <div
                        className="absolute top-3.5 right-4 text-[9px] font-black tracking-[0.15em] px-2 py-0.5 rounded"
                        style={{
                          background: 'linear-gradient(135deg, #b8860b, #d4af37, #f5c842)',
                          color: '#0a0718',
                        }}
                      >
                        #{order.id.slice(0, 8).toUpperCase()}
                      </div>

                      {/* Order content */}
                      <div className="mt-6 mb-4">
                        <div className="font-bold text-xl" style={{ color: '#f5e6c8' }}>
                          {order.items?.map(item => item.variant?.product?.name).filter(Boolean).join(', ')}
                        </div>
                        <div className="text-sm mt-1" style={{ color: '#7a5c30' }}>
                          {order.items?.map(item => `${item.variant?.name || item.variant?.product?.name} × ${item.quantity}`).join(' · ')}
                          {order.isSubscription && (
                            <span className="ml-2 text-[10px] font-black px-2 py-0.5 rounded-full align-middle"
                              style={{ background: 'rgba(212,175,55,0.15)', color: '#d4af37' }}>
                              ✦ EPIC Sub
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-3 mt-3 flex-wrap">
                          <span className="text-2xl font-black" style={{ color: '#d4af37' }}>
                            {fmt(order.totalInCents)}
                          </span>
                          <span className="text-xs" style={{ color: '#7a5c30' }}>
                            {formatDate(order.createdAt)}
                          </span>
                        </div>
                      </div>

                      <TrackingSection order={order} />

                      <OrderMessages orderId={order.id} />
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
      <Footer />
      {chatOrder && (
        <CustomerChatBubble
          orderId={chatOrder.id}
          orderLabel={'#' + chatOrder.id.slice(0, 8).toUpperCase()}
        />
      )}
    </div>
  );
}
